import React, {
  useMemo,
  useState,
  useCallback,
  useRef,
  useEffect
} from "react";
import { UserData } from "@/entities/users/types";
import { useReport } from "@/entities/reporting/hooks";
import {
  AverageTimeSpentData,
  AverageTimeSpentDirectReportsResponse,
  ReportType,
  SettingsPeriod
} from "@/entities/reporting/types";
import { camelCase, debounce, isEmpty } from "lodash";
import { useTranslation } from "react-i18next";
import StageAverageDuration, {
  StageAverageDurationChartDataType
} from "./StageAverageDuration";
import { createDataMapById } from "@/utils/datamaps";
import { DealMilestone } from "@/pages/deals/types";
import { daysToWeeks } from "date-fns";
import { useDealStages } from "@/entities/settings/deal/hooks";
import { useDirectReportsCheck } from "@/components/shared/useDirectReportsCheck";

interface Props {
  userData?: UserData;
  isAE: boolean;
}

const StageAverageDurationWrapper: React.FC<Props> = ({ userData, isAE }) => {
  const { t } = useTranslation();

  const stages = useDealStages().data?.stages;
  const [reportPeriod, setReportPeriod] = useState(SettingsPeriod.ALL_TIMES);

  // Keep track of the last successful data using ref to avoid re-render loops
  const lastValidReportRef = useRef<AverageTimeSpentData | null>(null);

  const { report, isLoading } = useReport(
    ReportType.AVG_TIME_SPENT,
    reportPeriod
  );

  const { hasDirectReports, directReportsData } =
    useDirectReportsCheck<AverageTimeSpentDirectReportsResponse>(
      userData?.id,
      reportPeriod,
      !isAE,
      {
        reportType: ReportType.AVG_TIME_SPENT,
        responseKey: "avgTimeSpent"
      }
    );

  const debouncedPeriodSelect = useCallback(
    debounce((period: SettingsPeriod) => {
      setReportPeriod(period);
    }, 300),
    []
  );

  const stagesDataMap = useMemo(
    () => (stages ? createDataMapById(stages) : {}),
    [stages]
  );

  const currentReport = report?.avgTimeSpent ?? undefined;

  useEffect(() => {
    if (currentReport) {
      lastValidReportRef.current = currentReport;
    }
  }, [currentReport]);

  // Use current data if available, otherwise fall back to last valid data
  const averageTimeSpentReport: AverageTimeSpentData =
    currentReport ?? lastValidReportRef.current ?? undefined;

  const processedStages = useMemo(() => {
    if (
      !report?.avgTimeSpent ||
      !averageTimeSpentReport ||
      isEmpty(stagesDataMap)
    ) {
      return undefined;
    }

    // First map the stages to include stageOrder
    const stagesWithOrder = averageTimeSpentReport.stages
      .map((stage) => {
        const stageId = stage.id.toString();
        const stageData = stagesDataMap[stageId];
        if (!stageData) return null;

        return {
          ...stage,
          stageData,
          stageOrder: stageData.stageOrder
        };
      })
      .filter((stage): stage is NonNullable<typeof stage> => stage !== null)
      .sort((a, b) => a.stageOrder - b.stageOrder); // Sort by stageOrder

    // Then reduce to build the cumulative start positions
    return stagesWithOrder.reduce<StageAverageDurationChartDataType[]>(
      (acc, stage) => {
        const prevStart = acc.length
          ? acc[acc.length - 1].start + acc[acc.length - 1].duration
          : 0;

        acc.push({
          id: stage.id.toString(),
          duration: daysToWeeks(stage.averageTimeSpentValues.you || 0),
          start: prevStart,
          label: stage.stageData.name ?? t("unknown"),
          milestone: stage.stageData.milestone,
          team: daysToWeeks(stage.averageTimeSpentValues.team || 0),
          org: daysToWeeks(stage.averageTimeSpentValues.org || 0)
        });

        return acc;
      },
      []
    );
  }, [stagesDataMap, report, averageTimeSpentReport]);

  const processedMilestones = useMemo(() => {
    if (
      !report?.avgTimeSpent ||
      !averageTimeSpentReport ||
      !processedStages ||
      isEmpty(averageTimeSpentReport.milestones)
    ) {
      return undefined;
    }

    const milestonesWithStages = new Set(
      processedStages.map((stage) => Number(stage.milestone))
    );

    return averageTimeSpentReport.milestones
      .filter((milestone) => milestonesWithStages.has(milestone.id))
      .reduce<StageAverageDurationChartDataType[]>((acc, milestone) => {
        const milestoneId = milestone.id as DealMilestone;
        const prevStart = acc.length
          ? acc[acc.length - 1].start + acc[acc.length - 1].duration
          : 0;

        acc.push({
          id: milestoneId.toString(),
          duration: daysToWeeks(milestone.averageTimeSpentValues.you || 0),
          start: prevStart,
          label: t(
            `deals.dealView.milestone.${camelCase(DealMilestone[milestoneId])}`
          ),
          milestone: milestoneId,
          team: daysToWeeks(milestone.averageTimeSpentValues.team || 0),
          org: daysToWeeks(milestone.averageTimeSpentValues.org || 0),
          industry: daysToWeeks(milestone.averageTimeSpentValues.industry || 0)
        });

        return acc;
      }, []);
  }, [report, averageTimeSpentReport, processedStages]);

  const processedReport = useMemo(() => {
    return {
      stages: processedStages ?? [],
      milestones: processedMilestones ?? []
    };
  }, [report, processedStages, processedMilestones]);

  return (
    <StageAverageDuration
      chartData={processedReport}
      userData={userData}
      period={reportPeriod}
      periodSelect={debouncedPeriodSelect}
      isLoading={isLoading}
      isAE={isAE}
      drillDownHidden={!hasDirectReports}
      directReportsData={directReportsData}
    />
  );
};

export default StageAverageDurationWrapper;
